import { useState, useCallback, useEffect } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, Package, Truck, CheckCircle2, Clock, UserCheck, MapPin, Loader2, Zap, ShieldCheck, Mail, Smartphone, Warehouse, IndianRupee, CreditCard } from "lucide-react";
import { toast } from "sonner";
import StatusBadge, { statusConfig } from "../components/StatusBadge";
import OnTimeBadge from "../components/OnTimeBadge";
import ReviewWidget from "../components/ReviewWidget";
import PriceCard from "../components/PriceCard";
import HubPriceCard from "../components/HubPriceCard";
import LiveTrackingMap from "../components/LiveTrackingMap";
import ProofGallery from "../components/ProofGallery";
import PayDifferenceDialog from "../components/orders/PayDifferenceDialog";
import RefundTimelineCard from "../components/orders/RefundTimelineCard";
import HubRouteCard from "../components/orders/HubRouteCard";
import useWebSocket from "../hooks/useWebSocket";

const expressSteps = [
  { key: "pending", label: "Order Placed", icon: Clock, desc: "Waiting for agent assignment" },
  { key: "assigned", label: "Agent Assigned", icon: UserCheck, desc: "Agent is preparing for pickup" },
  { key: "picked_up", label: "Picked Up", icon: Package, desc: "Package collected from sender" },
  { key: "in_transit", label: "In Transit", icon: Truck, desc: "On the way to destination" },
  { key: "delivered", label: "Delivered", icon: CheckCircle2, desc: "Successfully delivered" },
];

const hubRoutedSteps = [
  { key: "pending", label: "Order Placed", icon: Clock, desc: "Waiting for pickup agent" },
  { key: "pickup_assigned", label: "Pickup Agent", icon: UserCheck, desc: "Pickup agent assigned" },
  { key: "picked_up", label: "Picked Up", icon: Package, desc: "Package collected from sender" },
  { key: "at_origin_hub", label: "Origin Hub", icon: Warehouse, desc: "Arrived at origin sorting hub" },
  { key: "in_linehaul", label: "In Linehaul", icon: Truck, desc: "Inter-hub transport in progress" },
  { key: "at_dest_hub", label: "Dest Hub", icon: Warehouse, desc: "Arrived at destination hub" },
  { key: "out_for_delivery", label: "Out for Delivery", icon: Truck, desc: "Delivery agent en route" },
  { key: "delivered", label: "Delivered", icon: CheckCircle2, desc: "Successfully delivered" },
];

const stepColors = {
  pending: { active: "bg-slate-500", complete: "bg-slate-400", line: "bg-slate-300" },
  assigned: { active: "bg-blue-500", complete: "bg-blue-400", line: "bg-blue-300" },
  pickup_assigned: { active: "bg-blue-500", complete: "bg-blue-400", line: "bg-blue-300" },
  picked_up: { active: "bg-[#FF6B00]", complete: "bg-[#FF6B00]", line: "bg-orange-300" },
  at_origin_hub: { active: "bg-amber-500", complete: "bg-amber-400", line: "bg-amber-300" },
  in_linehaul: { active: "bg-violet-500", complete: "bg-violet-400", line: "bg-violet-300" },
  at_dest_hub: { active: "bg-teal-500", complete: "bg-teal-400", line: "bg-teal-300" },
  in_transit: { active: "bg-[#FF6B00]", complete: "bg-[#FF6B00]", line: "bg-orange-300" },
  out_for_delivery: { active: "bg-[#FF6B00]", complete: "bg-[#FF6B00]", line: "bg-orange-300" },
  delivered: { active: "bg-green-500", complete: "bg-green-400", line: "bg-green-300" },
};

const expressStepIndex = { pending: 0, assigned: 1, picked_up: 2, in_transit: 3, delivered: 4 };
const hubStepIndex = { pending: 0, pickup_assigned: 1, picked_up: 2, at_origin_hub: 3, in_linehaul: 4, at_dest_hub: 5, out_for_delivery: 6, delivered: 7 };

export default function Tracking() {
  const [trackingId, setTrackingId] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [agentLoc, setAgentLoc] = useState(null);
  const [channels, setChannels] = useState({ in_app: true, websocket: true, sms: false, email: false });
  const [payOpen, setPayOpen] = useState(false);

  useEffect(() => {
    api.get("/notifications/channels").then(({ data }) => setChannels(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; setters are stable.
  }, []);

  // Listen for real-time agent location updates
  const onWsMessage = useCallback((data) => {
    if (data.type === "agent_location" && order && data.tracking_id === order.tracking_id) {
      setAgentLoc({ lat: data.lat, lng: data.lng });
    }
    if (data.type === "order_update" && order && data.tracking_id === order.tracking_id) {
      setOrder((prev) => prev ? { ...prev, status: data.status } : prev);
      toast.info(data.message);
    }
    if (data.type === "delivery_otp" && order && data.tracking_id === order.tracking_id) {
      setOrder((prev) => prev ? { ...prev, delivery_otp: data.otp } : prev);
      toast.success(`Your delivery OTP: ${data.otp}`, { duration: 10000 });
    }
    // Sprint 72 — Trust & Transparency: pickup OTP and proof events.
    if (data.type === "pickup_otp" && order && data.tracking_id === order.tracking_id) {
      setOrder((prev) => prev ? { ...prev, pickup_otp: data.otp } : prev);
      toast.success(`Your pickup OTP: ${data.otp}. Share it with the agent at handover.`, { duration: 12000 });
    }
    if (data.type === "proof_event" && order && data.tracking_id === order.tracking_id) {
      const ev = data.event || {};
      toast.info(ev.label || "New proof event uploaded");
      // soft refetch in background to pick up new photo URLs
      api.get(`/orders/${order.tracking_id}`).then(({ data: fresh }) => setOrder(fresh)).catch(() => {});
    }
  }, [order]);

  useWebSocket(onWsMessage);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackingId.trim()) return;
    setLoading(true);
    setOrder(null);
    setSearched(true);
    try {
      const { data } = await api.get(`/orders/${trackingId.trim()}`);
      // Enrich with public-tracking extras (hub_journey + agent blocks
      // + pickup/delivery locations + active_agent) needed by the new
      // animated map. Fire-and-forget so a failure doesn't block the
      // basic page from rendering.
      api.get(`/track/${trackingId.trim()}`)
        .then(({ data: pub }) => {
          setOrder((prev) => prev ? {
            ...prev,
            hub_journey: pub.hub_journey || prev.hub_journey,
            pickup_agent: pub.pickup_agent || prev.pickup_agent,
            delivery_agent: pub.delivery_agent || prev.delivery_agent,
            active_agent: pub.active_agent || prev.active_agent,
            pickup_location: prev.pickup_location || pub.pickup_location,
            delivery_location: prev.delivery_location || pub.delivery_location,
            agent_location: prev.agent_location || pub.agent_location,
          } : prev);
        })
        .catch(() => { /* enrichment is best-effort; basic page still works */ });
      setOrder(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Order not found");
    } finally {
      setLoading(false);
    }
  };

  const isHubRouted = order?.delivery_mode === "hub_routed";
  const steps = isHubRouted ? hubRoutedSteps : expressSteps;
  const currentStep = order ? ((isHubRouted ? hubStepIndex : expressStepIndex)[order.status] ?? 0) : -1;

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="tracking-page">
      {/* Header */}
      <div className="text-center animate-fadeInUp">
        <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <MapPin className="h-7 w-7 text-[#FF6B00]" />
        </div>
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900" data-testid="tracking-heading">
          Track Your Order
        </h1>
        <p className="text-sm text-slate-500 mt-2">Enter your tracking ID to see real-time delivery status</p>
        <div className="mt-4 flex justify-center">
          <OnTimeBadge />
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleTrack} className="flex gap-3 animate-fadeInUp stagger-1" data-testid="tracking-form">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Enter tracking ID (e.g., PKG-A1B2C3D4)"
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            className="rounded-xl border-slate-200 h-12 pl-11 text-sm font-mono focus:border-[#FF6B00] focus:ring-[#FF6B00]/20"
            data-testid="tracking-id-input"
          />
        </div>
        <Button type="submit" disabled={loading} className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl px-6 h-12 font-semibold shadow-sm shadow-[#FF6B00]/20 btn-press" data-testid="tracking-search-btn">
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Track"}
        </Button>
      </form>

      {/* Result */}
      {order && (
        <div className="space-y-6 animate-fadeInUp">
          {/* Sprint 91 — post-delivery review prompt for the booking customer */}
          <ReviewWidget trackingId={order.tracking_id} status={order.status} isOwner={Boolean(order.is_owner ?? true)} />
          {/* Order Info Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6" data-testid="tracking-order-info">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
              <div>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Tracking ID</p>
                <p className="font-mono text-lg font-bold text-slate-900 mt-0.5" data-testid="tracking-display-id">{order.tracking_id}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={order.status} size="md" />
                {order.price?.estimated_days && order.status !== "delivered" && (
                  <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-xl text-sm font-semibold" data-testid="tracking-eta-badge">
                    <Clock className="h-4 w-4" />
                    ~{order.price.estimated_days === 1 ? "1 day" : `${order.price.estimated_days} days`}
                  </span>
                )}
              </div>
            </div>

            {/* ETA Banner */}
            {order.price?.estimated_days && order.status !== "delivered" && (
              <div className="flex items-center gap-3 bg-emerald-50/70 border border-emerald-200/60 rounded-xl p-3.5 mb-5" data-testid="tracking-eta-banner">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
                  <Zap className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-emerald-800">
                    Estimated delivery in {order.price.estimated_days === 1 ? "1 day" : `${order.price.estimated_days} days`}
                  </p>
                  <p className="text-xs text-emerald-600 mt-0.5">
                    {order.price.distance_km || order.price.total_distance_km || 0} km away
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-4">
                <InfoRow label="Package" value={order.package_description} />
                <InfoRow label="Size" value={`${order.item_size}${order.package_weight ? ` / ${order.package_weight}kg` : ""}`} capitalize />
                {order.ai_dimensions && (
                  <div data-testid="tracking-ai-dims">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">AI Detected</p>
                    <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 space-y-0.5">
                      {order.ai_dimensions.dimensions_cm && (
                        <p className="text-xs font-semibold text-blue-700">{order.ai_dimensions.dimensions_cm}</p>
                      )}
                      {order.ai_dimensions.item_detected && (
                        <p className="text-[10px] text-blue-600">{order.ai_dimensions.item_detected}</p>
                      )}
                      {order.ai_dimensions.items_estimate && (
                        <p className="text-[10px] text-blue-600">{order.ai_dimensions.items_estimate}</p>
                      )}
                      {order.ai_dimensions.crew_size && (
                        <p className="text-[10px] text-blue-600">{order.ai_dimensions.crew_size} crew members assigned</p>
                      )}
                    </div>
                  </div>
                )}
                {order.delivery_mode === "hub_routed" ? (
                  <>
                    {order.pickup_agent_name && <InfoRow label="Pickup Agent" value={`${order.pickup_agent_name}${order.pickup_agent_phone ? ' (' + order.pickup_agent_phone + ')' : ''}`} />}
                    {order.delivery_agent_name && <InfoRow label="Delivery Agent" value={`${order.delivery_agent_name}${order.delivery_agent_phone ? ' (' + order.delivery_agent_phone + ')' : ''}`} />}
                    {!order.pickup_agent_name && !order.delivery_agent_name && <InfoRow label="Agents" value="Awaiting assignment" />}
                  </>
                ) : (
                  order.agent_name && <InfoRow label="Agent" value={`${order.agent_name}${order.agent_phone ? ' (' + order.agent_phone + ')' : ''}`} />
                )}
              </div>
              <div className="space-y-4">
                <InfoRow label="Pickup" value={order.pickup_address} />
                <InfoRow label="Delivery" value={order.delivery_address} />
                {order.price && (
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Total Price</p>
                    {order.delivery_mode === "hub_routed" ? (
                      <HubPriceCard pricing={order.hub_pricing || order.price} compact />
                    ) : (
                      <PriceCard price={order.price} compact />
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Multi-city scalability — hub route visual for hub-routed orders */}
            {order.delivery_mode === "hub_routed" && order.tracking_id && (
              <div className="mt-4">
                <HubRouteCard trackingId={order.tracking_id} />
              </div>
            )}
          </div>

          {/* Sprint 80 — Pay difference banner (post-edit upcharge) */}
          {(() => {
            const pendingAdd = (order.pending_settlements || [])
              .filter((p) => p?.status === "pending")
              .reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
            if (!pendingAdd) return null;
            return (
              <div
                className="bg-gradient-to-br from-amber-50 to-orange-50/60 border-2 border-amber-200 rounded-2xl p-5 md:p-6 animate-scaleIn"
                data-testid="tracking-pending-settlement-card"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center shrink-0">
                    <IndianRupee className="h-5 w-5 text-amber-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Outfit'] text-base font-bold text-slate-900">
                      Pay difference to unblock your order
                    </h3>
                    <p className="text-xs text-slate-600 mt-1">
                      Your recent order edit increased the price. Pay the additional amount to let the order proceed past pickup.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-white rounded-xl border border-amber-200 p-4">
                  <div className="flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                      Amount due
                    </p>
                    <p className="text-3xl font-extrabold text-amber-900" data-testid="tracking-pending-amount">
                      ₹{pendingAdd}
                    </p>
                  </div>
                  <Button
                    onClick={() => setPayOpen(true)}
                    className="bg-amber-600 text-white hover:bg-amber-700 rounded-xl px-5 h-11 font-semibold shadow-sm shadow-amber-600/20 btn-press"
                    data-testid="tracking-pay-difference-btn"
                  >
                    <CreditCard className="h-4 w-4 mr-1.5" /> Pay ₹{pendingAdd}
                  </Button>
                </div>
              </div>
            );
          })()}

          {/* Sprint 81 — Refund Timeline (cancellation + edit-reprice refunds) */}
          {order?.tracking_id && (
            <RefundTimelineCard trackingId={order.tracking_id} />
          )}

          {/* Sprint 72 — Pickup OTP Display for Customer (hub-routed flow) */}
          {order.pickup_otp && (order.status === "pickup_assigned" || order.status === "assigned") && !order.pickup_otp_verified_at && (
            <div className="bg-gradient-to-br from-orange-50 via-amber-50/50 to-white border-2 border-orange-200 rounded-2xl p-5 md:p-6 animate-scaleIn" data-testid="tracking-pickup-otp-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-['Outfit'] text-base font-semibold text-slate-900">Pickup OTP</h3>
                  <p className="text-xs text-slate-500">Share this with your pickup agent at handover</p>
                </div>
              </div>
              <div className="bg-white border border-orange-200 rounded-xl p-4 text-center">
                <p className="font-mono text-3xl sm:text-4xl font-bold text-orange-700 tracking-[0.3em]" data-testid="tracking-pickup-otp-code">
                  {order.pickup_otp}
                </p>
              </div>
              <p className="text-xs text-orange-600 text-center mt-3">
                The agent will enter this code to confirm pickup
              </p>
            </div>
          )}

          {/* Sprint 73-fix — Proof photos visible to logged-in customer */}
          {hasAnyProof(order) && (
            <div className="bg-white border border-slate-100 rounded-2xl p-5 md:p-6 shadow-sm" data-testid="tracking-proof-photos">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <ShieldCheck className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-['Outfit'] text-base font-semibold text-slate-900">Proof of journey</h3>
                  <p className="text-xs text-slate-500">Every step photographed — tap any image to enlarge</p>
                </div>
              </div>
              <ProofGallery order={order} size="md" />
            </div>
          )}

          {/* OTP Display for Customer */}
          {order.delivery_otp && (order.status === "in_transit" || order.status === "out_for_delivery") && (
            <div className="bg-gradient-to-br from-green-50 via-emerald-50/50 to-white border-2 border-green-200 rounded-2xl p-5 md:p-6 animate-scaleIn" data-testid="tracking-otp-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-['Outfit'] text-base font-semibold text-slate-900">Delivery OTP</h3>
                  <p className="text-xs text-slate-500">Share this code with the delivery agent</p>
                </div>
              </div>
              <div className="bg-white border border-green-200 rounded-xl p-4 text-center">
                <p className="font-mono text-3xl sm:text-4xl font-bold text-green-700 tracking-[0.3em]" data-testid="tracking-otp-code">
                  {order.delivery_otp}
                </p>
              </div>
              <p className="text-xs text-green-600 text-center mt-3">
                The agent will need this code to confirm your delivery
              </p>
              <div className="flex items-center justify-center gap-3 mt-3" data-testid="otp-channels">
                <span className="inline-flex items-center gap-1 text-[10px] text-green-600 font-medium">
                  <CheckCircle2 className="h-3 w-3" /> In-App
                </span>
                {channels.email && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-blue-600 font-medium">
                    <Mail className="h-3 w-3" /> Email Sent
                  </span>
                )}
                {channels.sms && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-purple-600 font-medium">
                    <Smartphone className="h-3 w-3" /> SMS Sent
                  </span>
                )}
                {!channels.email && !channels.sms && (
                  <span className="text-[10px] text-slate-400">SMS & Email available when configured</span>
                )}
              </div>
            </div>
          )}

          {/* Live Map */}
          {order.pickup_location && order.delivery_location && (
            <LiveTrackingMap
              pickup={order.pickup_location}
              delivery={order.delivery_location}
              agentLocation={agentLoc || order.agent_location}
              agentName={order.agent_name}
              agentPhone={order.agent_phone}
              status={order.status}
              estimatedDays={order.price?.estimated_days}
              hubJourney={order.hub_journey}
              pickupAgent={order.pickup_agent}
              deliveryAgent={order.delivery_agent}
              deliveryOtp={order.delivery_otp}
              demoMode
              className="h-[420px]"
            />
          )}

          {/* Progress Steps */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 md:p-6" data-testid="tracking-progress">
            <h3 className="font-['Outfit'] text-base font-semibold text-slate-900 mb-6">Delivery Progress</h3>
            <div className="space-y-0">
              {steps.map((step, idx) => {
                const Icon = step.icon;
                const isComplete = idx <= currentStep;
                const isCurrent = idx === currentStep;
                const isLast = idx === steps.length - 1;
                const colors = stepColors[step.key];
                // Extracted from nested ternary (#8 code review) for readability.
                const getStepIconBg = () => {
                  if (isCurrent) return `${colors.active} shadow-md`;
                  if (isComplete) return colors.complete;
                  return "bg-slate-100";
                };
                const getStepLabelText = () => {
                  if (isCurrent) return "text-slate-900";
                  if (isComplete) return "text-slate-700";
                  return "text-slate-400";
                };
                return (
                  <div key={step.key} className="flex gap-4" data-testid={`tracking-step-${step.key}`}>
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${getStepIconBg()}`}>
                        <Icon className={`h-5 w-5 ${isComplete || isCurrent ? "text-white" : "text-slate-400"}`} />
                      </div>
                      {!isLast && (
                        <div className={`w-0.5 h-8 my-1 rounded transition-colors duration-300 ${
                          isComplete && idx < currentStep ? colors.line : "bg-slate-200"
                        }`} />
                      )}
                    </div>
                    <div className={isLast ? "pb-0" : "pb-6"}>
                      <p className={`text-sm font-semibold transition-colors ${getStepLabelText()}`}>
                        {step.label}
                        {isCurrent && <span className="ml-2 text-[10px] bg-orange-100 text-[#FF6B00] px-1.5 py-0.5 rounded-full font-bold">CURRENT</span>}
                      </p>
                      <p className={`text-xs mt-0.5 ${isComplete ? "text-slate-500" : "text-slate-400"}`}>{step.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Price Breakdown */}
          {order.price && (
            <div className="bg-gradient-to-br from-orange-50 via-amber-50/50 to-white border border-orange-200/80 rounded-2xl p-5 md:p-6 price-glow animate-scaleIn" data-testid="tracking-price-card">
              <h3 className="font-['Outfit'] text-base font-semibold text-slate-900 mb-4">Price Breakdown</h3>
              {order.delivery_mode === "hub_routed" ? (
                <HubPriceCard pricing={order.hub_pricing || order.price} />
              ) : (
                <div className="space-y-2.5 text-sm">
                  {(order.price.pickup_packing_charge ?? 0) > 0 && (
                    <div className="flex justify-between text-slate-500">
                      <span>Service Charges <span className="text-[10px] text-slate-400">(Pickup + Packing)</span></span>
                      <span className="font-medium text-slate-700">INR {order.price.pickup_packing_charge}</span>
                    </div>
                  )}
                  {(order.price.weight_charge_display ?? order.price.weight_charge) > 0 && (
                    <div className="flex justify-between text-slate-500">
                      <span>Weight Charges <span className="text-[10px] text-slate-400">(Slab: {order.price.weight_slab || `${order.price.chargeable_weight_kg || 0} kg`})</span></span>
                      <span className="font-medium text-slate-700">INR {order.price.weight_charge_display ?? order.price.weight_charge}</span>
                    </div>
                  )}
                  {(order.price.delivery_charge ?? 0) > 0 && (
                    <div className="flex justify-between text-slate-500">
                      <span>Service Charges <span className="text-[10px] text-slate-400">(Zone {order.price.zone || "A"})</span></span>
                      <span className="font-medium text-slate-700">INR {order.price.delivery_charge}</span>
                    </div>
                  )}
                  {order.price.gst > 0 && (
                    <div className="flex justify-between text-slate-500">
                      <span>GST {order.price.gst_rate_pct ?? 18}%</span>
                      <span className="font-medium text-slate-700">INR {order.price.gst}</span>
                    </div>
                  )}
                  <div className="border-t border-orange-200/60 pt-3 mt-2 flex justify-between items-center">
                    <span className="font-bold text-slate-900 tracking-tight">TOTAL</span>
                    <span className="font-extrabold text-[#FF6B00] text-xl tracking-tight">INR {order.price.final_price ?? order.price.total}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {searched && !order && !loading && (
        <div className="text-center py-12 animate-fadeInUp" data-testid="tracking-not-found">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Search className="h-8 w-8 text-slate-300" />
          </div>
          <p className="text-base font-semibold text-slate-700">No order found</p>
          <p className="text-sm text-slate-400 mt-1">Double-check the tracking ID and try again</p>
        </div>
      )}

      {/* Sprint 80 — Pay difference dialog */}
      <PayDifferenceDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        trackingId={order?.tracking_id}
        onPaid={async (fresh) => {
          if (fresh) {
            setOrder(fresh);
          } else if (order?.tracking_id) {
            try {
              const { data } = await api.get(`/orders/${order.tracking_id}`);
              setOrder(data);
            } catch { /* noop */ }
          }
        }}
      />
    </div>
  );
}

function InfoRow({ label, value, capitalize }) {
  return (
    <div>
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">{label}</p>
      <p className={`text-sm font-medium text-slate-800 ${capitalize ? "capitalize" : ""}`}>{value}</p>
    </div>
  );
}

function hasAnyProof(order) {
  if (!order) return false;
  return Boolean(
    order.customer_package_photo ||
    order.origin_hub_handover_photo ||
    order.packing_photo ||
    order.destination_arrival_photo ||
    (order.proof_events || []).some((e) => e.image_base64)
  );
}
